/**
 * What the Network page, the monitor page and the device page's adopt panel say about a discovered
 * peer or a monitor - kept out of the pages so it is under test, and so the three cannot disagree.
 *
 * Hand ports of `DiscoveredRowViewModel`'s rules and labels, `WakeOnLan.NormalizeMac` and the monitor
 * page's byte and uptime formatting.
 */
import { DiscoveredDeviceKind, type DiscoveredDeviceDto } from './contracts.gen.js';
import { join } from './format.js';
import type { ChipTone } from './ui/Chip.js';

export function kindLabel(kind: DiscoveredDeviceKind): string {
  switch (kind) {
    case DiscoveredDeviceKind.Computer:
      return 'Computer';
    case DiscoveredDeviceKind.Printer:
      return 'Printer';
    case DiscoveredDeviceKind.NetworkDevice:
      return 'Network device';
    case DiscoveredDeviceKind.Nas:
      return 'NAS';
    case DiscoveredDeviceKind.Ups:
      return 'UPS';
    case DiscoveredDeviceKind.IpCamera:
      return 'IP camera';
    case DiscoveredDeviceKind.SynologyNas:
      return 'Synology NAS';
    case DiscoveredDeviceKind.SynologyRouter:
      return 'Synology router';
    default:
      return 'SNMP device';
  }
}

/** The kinds a monitor's edit panel offers, in order; anything else reads as "SNMP device". */
export const MONITOR_KINDS: readonly DiscoveredDeviceKind[] = [
  DiscoveredDeviceKind.Printer,
  DiscoveredDeviceKind.Ups,
  DiscoveredDeviceKind.NetworkDevice,
  DiscoveredDeviceKind.Nas,
  DiscoveredDeviceKind.IpCamera,
  DiscoveredDeviceKind.Computer,
  DiscoveredDeviceKind.SynologyNas,
  DiscoveredDeviceKind.SynologyRouter,
  DiscoveredDeviceKind.Other
];

export function isSynologyKind(kind: DiscoveredDeviceKind): boolean {
  return kind === DiscoveredDeviceKind.SynologyNas || kind === DiscoveredDeviceKind.SynologyRouter;
}

/**
 * Whether a peer is a Synology - by the classifier's kind, and by its SNMP identity strings so a
 * device discovered before the Synology kinds existed is still recognised without a re-scan.
 */
export function looksSynology(peer: DiscoveredDeviceDto): boolean {
  if (isSynologyKind(peer.kind)) return true;
  const haystack = `${peer.snmpDescription} ${peer.snmpSysName} ${peer.hostname}`.toLowerCase();
  return ['synology', 'diskstation', 'rackstation', 'flashstation'].some(word => haystack.includes(word));
}

/** The SNMP system name and description, shown under the host when present. */
export function snmpSummary(peer: DiscoveredDeviceDto): string {
  if (!peer.snmpEnabled) return '';
  return join([peer.snmpSysName || null, peer.snmpDescription || null], ' — ');
}

/**
 * The canonical MAC (uppercase, colon-separated), or null for anything that does not identify a
 * machine: the wrong length, non-hex, all zeroes or all ones - the last two appear in inventories for
 * adapters that have none. The port of `WakeOnLan.NormalizeMac`; a wake is offered only when this is
 * non-null, since a magic packet is addressed to a hardware address.
 */
export function normalizeMac(mac: string | null | undefined): string | null {
  if (!mac?.trim()) return null;
  const hex = mac.replace(/[:\-. ]/g, '');
  if (!/^[0-9a-fA-F]{12}$/.test(hex)) return null;
  const upper = hex.toUpperCase();
  if (/^0{12}$/.test(upper) || /^F{12}$/.test(upper)) return null;
  return upper.match(/../g)!.join(':');
}

export function stateLabel(peer: DiscoveredDeviceDto): string {
  if (peer.isManaged) return 'Managed';
  return peer.supportsPushInstall ? 'Unmanaged' : 'Unmanaged (manual install only)';
}

/** Generic SNMP monitoring: anything that answers SNMP and is not adopted - except a Synology. */
export function canAdopt(peer: DiscoveredDeviceDto): boolean {
  return peer.snmpEnabled && peer.adoptedMonitorId === null && !looksSynology(peer);
}

export function canEnrollSynology(peer: DiscoveredDeviceDto): boolean {
  return looksSynology(peer) && peer.adoptedMonitorId === null;
}

/** Push-install only where a Windows management port answered (135 or 5985/5986), never 445 alone. */
export function canDeploy(peer: DiscoveredDeviceDto): boolean {
  return !peer.isManaged && peer.supportsPushInstall;
}

/** "1.4 TB" - the monitor page's sizes; a missing size is a dash, not "0 B". */
export function formatBytes(bytes: number): string {
  if (!(bytes > 0)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return value >= 100 || unit === 0 ? `${Math.round(value)} ${units[unit]}` : `${value.toFixed(1)} ${units[unit]}`;
}

export function formatUptime(seconds: number): string {
  if (!(seconds > 0)) return '';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return days >= 1 ? `${days}d ${hours}h ${minutes}m` : `${hours}h ${minutes}m`;
}

/** Green until it matters, amber past 75%, red past 90% - where a NAS gets urgent. */
export function volumeTone(usedPercent: number): ChipTone {
  return usedPercent >= 90 ? 'danger' : usedPercent >= 75 ? 'warn' : 'ok';
}
